import appendQuery from 'append-query';
import type { ThunkAction } from 'redux-thunk';
import type { AnyAction } from 'redux';

import { FETCH_AMAZON_REVERSE_SHARING, RECEIVE_AMAZON_REVERSE_SHARING, RECEIVE_AMAZON_REVERSE_SHARING_ERROR } from 'constants/reduxActions';
import { getAmazonLinkedStyles } from 'apis/amazonReverseSharing';
import type { AmazonLinkedStylesResponse } from 'apis/amazonReverseSharing';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import type { AmazonReverseSharingProduct } from 'reducers/amazonReverseSharing';
import type { AppState } from 'types/app';
import marketplace from 'cfg/marketplace.json';

// Only zappos has api.amazonReverseSharing — safe access prevents 6pm build from crashing at import time
const AMAZON_REVERSE_SHARING_URL = marketplace.api?.amazonReverseSharing?.url;

function fetchingAmazonReverseSharing() {
  return { type: FETCH_AMAZON_REVERSE_SHARING } as const;
}

function receiveAmazonReverseSharing(data: { linkedStyles: AmazonReverseSharingProduct[]; productId: string }) {
  return { data, type: RECEIVE_AMAZON_REVERSE_SHARING } as const;
}

function receiveAmazonReverseSharingError(error: string) {
  return { error, type: RECEIVE_AMAZON_REVERSE_SHARING_ERROR } as const;
}

export type AmazonReverseSharingAction =
  | ReturnType<typeof fetchingAmazonReverseSharing>
  | ReturnType<typeof receiveAmazonReverseSharing>
  | ReturnType<typeof receiveAmazonReverseSharingError>;

/**
 * Transforms the raw API response into the shape consumed by the carousel component.
 * Mirrors the DTO logic from kratos: @mweb/services/amazonReverseSharing/dto.ts
 */
function transformLinkedStyles(response: AmazonLinkedStylesResponse): { linkedStyles: AmazonReverseSharingProduct[]; productId: string } {
  const linkedStyles: AmazonReverseSharingProduct[] = response.linkedStyles.map(style => ({
    brandName: style.brandName,
    imageId: style.imageId,
    link: style.link,
    msaImageId: style.imageId,
    onHand: 999,
    originalPrice: style.originalPrice,
    percentOff: style.percentOff,
    price: style.price,
    productId: style.productId,
    productName: style.productName,
    productSeoUrl: appendQuery(style.link, 'ref=amazon_reverse_shared'),
    productUrl: appendQuery(style.productUrl, 'ref=amazon_reverse_shared'),
    styleId: style.styleId
  }));

  return { linkedStyles, productId: response.productId };
}

/**
 * Fetches Amazon reverse sharing linked styles for a product.
 * Uses the amazonReverseSharing.url from cfg/marketplace.json
 * (https://beta-api.cloudcatalog.zappos.com — distinct from the regular cloudcatalog URL at https://api.cloudcatalog.zappos.com).
 */
export function fetchAmazonReverseSharing(productId: string): ThunkAction<Promise<void>, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    if (!productId) {
      return Promise.resolve();
    }

    const { cookies = {} } = getState();

    dispatch(fetchingAmazonReverseSharing());

    return getAmazonLinkedStyles(AMAZON_REVERSE_SHARING_URL, { productId }, undefined, cookies)
      .then(fetchErrorMiddleware)
      .then((json: AmazonLinkedStylesResponse) => {
        if (!json || !json.linkedStyles) {
          dispatch(receiveAmazonReverseSharingError('No linked styles data'));
          return;
        }
        dispatch(receiveAmazonReverseSharing(transformLinkedStyles(json)));
      })
      .catch((err: Error) => {
        dispatch(receiveAmazonReverseSharingError(err.message || 'Failed to fetch linked styles'));
      });
  };
}
